import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ENHANCEMENT_TEMPLATES = {
  'text-to-game': (p) => `${p} — with detailed pixel art visuals, smooth gameplay mechanics, progressive difficulty levels, particle effects, sound design, and polished UI. Ultra-detailed game world with immersive atmosphere.`,
  'prompt-to-mini-game': (p) => `${p} — fast-paced arcade style with colorful visuals, satisfying feedback effects, score system, and addictive gameplay loop. Mobile-friendly controls.`,
  'text-to-animation': (p) => `${p} — cinematic quality animation with smooth 60fps motion, dynamic lighting, detailed character expressions, atmospheric depth, and professional color grading.`,
  'script-to-video': (p) => `${p} — cinematic 4K quality with dramatic lighting, atmospheric fog, ultra-detailed environments, professional color grading, and immersive sound design.`,
  'music-to-video': (p) => `${p} — synchronized visual experience with beat-reactive elements, dynamic color palette, smooth transitions, particle effects, and cinematic camera movements.`,
  'text-to-music': (p) => `${p} — professionally mixed and mastered, with layered instrumentation, dynamic arrangement, emotional depth, and crystal-clear audio quality.`,
  'lip-sync': (p) => `${p} — ultra-realistic facial animation with precise lip synchronization, natural head movement, expressive eye animation, and cinematic lighting.`,
  'image-to-video': (p) => `${p} — cinematic animation with smooth camera movement, dynamic lighting effects, atmospheric particles, depth-of-field blur, and environmental motion.`,
};

export default function PromptEnhancer({ prompt, mode, onEnhancedPrompt, disabled }) {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanced, setEnhanced] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  const handleEnhance = async () => {
    if (!prompt?.trim() || isEnhancing) return;
    setIsEnhancing(true);
    setEnhanced(null);

    // Try OpenAI enhancement first
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    let enhancedText = null;

    if (apiKey && apiKey !== 'your-openai-api-key-here') {
      try {
        const response = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
          body: JSON.stringify({
            model: 'gpt-3.5-turbo',
            messages: [
              { role: 'system', content: `You are an AI prompt enhancement specialist for a ${mode} creation platform. Expand and improve the user's prompt to be more detailed, cinematic, and visually rich. Keep it under 150 words. Return only the enhanced prompt, no explanation.` },
              { role: 'user', content: prompt },
            ],
            max_tokens: 200,
            temperature: 0.7,
          }),
        });
        if (response?.ok) {
          const data = await response?.json();
          enhancedText = data?.choices?.[0]?.message?.content?.trim();
        }
      } catch {}
    }

    // Fallback to template enhancement
    if (!enhancedText) {
      await new Promise((r) => setTimeout(r, 1200));
      const template = ENHANCEMENT_TEMPLATES?.[mode] || ENHANCEMENT_TEMPLATES?.['text-to-game'];
      enhancedText = template(prompt);
    }

    setEnhanced(enhancedText);
    setShowComparison(true);
    setIsEnhancing(false);
  };

  const handleApply = () => {
    if (enhanced) {
      onEnhancedPrompt?.(enhanced);
      setShowComparison(false);
      setEnhanced(null);
    }
  };

  if (!prompt?.trim()) return null;

  return (
    <div className="flex flex-col gap-2">
      {!showComparison ? (
        <button
          onClick={handleEnhance}
          disabled={isEnhancing || disabled}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-50"
          style={{ borderColor: 'rgba(139,92,246,0.4)', background: 'rgba(139,92,246,0.06)', color: '#8B5CF6', fontFamily: 'var(--font-caption)' }}
        >
          <Icon name={isEnhancing ? 'Loader' : 'Sparkles'} size={13} color="#8B5CF6" />
          {isEnhancing ? 'Enhancing prompt...' : '✨ Enhance Prompt with AI'}
        </button>
      ) : (
        <div className="rounded-xl border overflow-hidden animate-fade-in-up" style={{ borderColor: 'rgba(139,92,246,0.3)', background: 'rgba(139,92,246,0.04)' }}>
          <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: 'rgba(139,92,246,0.2)', background: 'rgba(139,92,246,0.08)' }}>
            <div className="flex items-center gap-1.5">
              <Icon name="Sparkles" size={13} color="#8B5CF6" />
              <span className="text-xs font-bold" style={{ color: '#8B5CF6', fontFamily: 'var(--font-heading)' }}>AI Enhanced Prompt</span>
            </div>
            <button onClick={() => setShowComparison(false)} className="w-5 h-5 rounded flex items-center justify-center hover:bg-[var(--color-muted)]">
              <Icon name="X" size={11} color="var(--color-muted-foreground)" />
            </button>
          </div>
          <div className="p-3 flex flex-col gap-2">
            <div className="p-2 rounded-lg" style={{ background: 'var(--color-muted)' }}>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Original:</p>
              <p className="text-xs" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }}>{prompt}</p>
            </div>
            <div className="p-2 rounded-lg" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>
              <p className="text-xs font-semibold mb-1" style={{ color: '#8B5CF6', fontFamily: 'var(--font-caption)' }}>Enhanced:</p>
              <p className="text-xs" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }}>{enhanced}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleApply}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-90"
                style={{ background: '#8B5CF6', color: 'white', fontFamily: 'var(--font-heading)' }}
              >
                <Icon name="Check" size={12} color="white" />
                Use Enhanced
              </button>
              <button
                onClick={() => setShowComparison(false)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border transition-all hover:opacity-80"
                style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
              >
                Keep Original
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
